package features.videoDetails

import analytics.Analytics
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.viewModelScope
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.model.Route
import crashreporting.CrashReporter
import data.VideoRepository
import features.videoDetails.data.FavoriteInteractor
import features.watch.WatchProgressInteractor
import com.kinoshazam.taste.TasteInteractor
import com.kinoshazam.taste.TasteSignalKind
import io.github.aakira.napier.Napier
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.launch
import models.FavoriteVideo
import models.VideoDetails
import org.koin.core.component.KoinComponent
import utils.BaseViewModel
import utils.launch

private const val TEST_VIDEO_URL =
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"

class VideoDetailsViewModel(
    private val favoriteInteractor: FavoriteInteractor,
    private val watchProgressInteractor: WatchProgressInteractor,
    private val tasteInteractor: TasteInteractor,
    private val moviesRepository: VideoRepository,
    private val savedStateHandle: SavedStateHandle,
    private val navigator: Navigator,
    private val globalStateRepository: GlobalStateRepository,
    private val analytics: Analytics,
    private val crashReporter: CrashReporter,
) : BaseViewModel<VideoDetailsState, DetailsMVIAction>(), KoinComponent {

    private val videoId = savedStateHandle.toRoute<Route.Dialog.Details>().id
    private val isSerial = savedStateHandle.toRoute<Route.Dialog.Details>().isSerial

    private val coroutineExceptionHandler = CoroutineExceptionHandler { _, error ->
        updateState { copy(isLoading = false, isError = true) }
        Napier.e(error.message ?: "!!!!!!! error")
        crashReporter.capture(error)
    }

    override fun initState(): VideoDetailsState = VideoDetailsState(
        features = globalStateRepository.features.value
    )

    init {
        featuresInit()
        loadAll()
    }

    override fun onAction(action: DetailsMVIAction) {
        when (action) {
            is DetailsMVIAction.ClickMore -> onMoreClicked()
            is DetailsMVIAction.Reload -> onReload()
            is DetailsMVIAction.OnSeasonClicked -> onSeasonClicked(action.season, action.videoId)
            is DetailsMVIAction.OnEpisodeClicked -> onEpisodeClicked(action.episode)
            is DetailsMVIAction.AddToFavorite -> onAddToFavorite(action.videoId)
            is DetailsMVIAction.OnPlayClicked -> onPlayClicked(action.url)
            is DetailsMVIAction.BackClicked -> navigator.back()
            is DetailsMVIAction.OnVideoClicked -> onVideoClicked(action.id)
            is DetailsMVIAction.OnActorClicked -> navigator.navigateTo(
                Route.Dialog.Person(action.personId)
            )
        }
    }

    private fun onMoreClicked() {
        updateState {
            copy(isFullDescription = !state.value.isFullDescription)
        }
    }

    private fun onReload() {
        updateState { copy(isLoading = true, isError = false) }
        loadAll()
    }

    private fun loadAll() {
        loadDetails()
        loadCast()
        loadSimilarVideo()
    }

    private fun onSeasonClicked(season: VideoDetails.Season, videoId: Int) {
        updateState {
            copy(
                selectedSeason = season,
                isEpisodesLoading = true,
            )
        }
        loadSeason(videoId, season.seasonNumber)
    }

    private fun onEpisodeClicked(episode: VideoDetails.Episode) {
        updateState { copy(selectedEpisode = episode) }
    }

    private fun onAddToFavorite(videoId: Int) {
        launch(onError = ::handleAddFavError) {
            if (stateValue.isFavorite) {
                favoriteInteractor.deleteFavorite(
                    FavoriteVideo(
                        videoId = videoId,
                        title = state.value.videoInfo?.title,
                        imageUrl = state.value.videoInfo?.imageUrl,
                        isTvShow = requireNotNull(state.value.videoInfo?.isTvShow),
                        isDeleted = true,
                    )
                )
                analytics.track(
                    AppEvents.favoriteToggled(
                        videoId = videoId,
                        isSerial = isSerial,
                        added = false,
                    )
                )
                updateState { copy(isFavorite = false) }
            } else {
                favoriteInteractor.addFavorite(
                    FavoriteVideo(
                        videoId = videoId,
                        title = state.value.videoInfo?.title,
                        imageUrl = state.value.videoInfo?.imageUrl,
                        isTvShow = requireNotNull(state.value.videoInfo?.isTvShow),
                        isDeleted = false,
                    )
                )
                analytics.track(
                    AppEvents.favoriteToggled(
                        videoId = videoId,
                        isSerial = isSerial,
                        added = true,
                    )
                )
                updateState { copy(isFavorite = true) }
                tasteInteractor.recordSignal(videoId.toString(), isSerial, TasteSignalKind.SAVED)
            }
        }
    }

    private fun onPlayClicked(url: String) {
        val info = state.value.videoInfo
        val imdbId = info?.imdbId.orEmpty()
        val season = if (isSerial) state.value.selectedSeason?.seasonNumber ?: 0 else 0
        val episode = if (isSerial) state.value.selectedEpisode?.episodeNumber ?: 0 else 0
        val playUrl = if (isSerial) {
            "https://api.tobaco.ws/embed/imdb/$imdbId?season=$season&episode=$episode&host=filmot.site&episodesOpen=false"
        } else {
            "https://api.tobaco.ws/embed/imdb/$imdbId?host=filmot.site"
        }
        analytics.track(
            AppEvents.playbackStarted(
                videoId = videoId,
                isSerial = isSerial,
                source = "web_embed",
            )
        )
        launch { tasteInteractor.recordSignal(videoId.toString(), isSerial, TasteSignalKind.PLAYED) }
        navigator.navigateTo(
            Route.Dialog.WebPlayer(
                url = playUrl,
                title = info?.title.orEmpty(),
                videoId = videoId,
                isTvShow = isSerial,
                seasonNumber = season,
                episodeNumber = episode,
                imdbId = imdbId,
                imageUrl = info?.imageUrl.orEmpty(),
                backdropUrl = info?.backdropUrl.orEmpty(),
            )
        )
        Unit
    }

    private fun onVideoClicked(id: Int) {
        navigator.navigateTo(
            Route.Dialog.Details(
                id,
                false
            )
        )
    }

    private fun handleAddFavError(e: Throwable) {
        val f = 0
        // TODO: show toast error adding
    }

    private fun onBackClicked() {
//        onFinished()
    }

    private fun loadDetails() {
        viewModelScope.launch(coroutineExceptionHandler) {
            val details = if (isSerial) {
                moviesRepository.getTvShowDetails(videoId = videoId)
            } else {
                moviesRepository.getMovieDetails(videoId = videoId)
            }

            val isFavorite = favoriteInteractor.isFavorite(videoId)
            val watchTarget = watchProgressInteractor.playbackTarget(videoId)
            val seasonNumber = when {
                isSerial && watchTarget != null && (watchTarget.seasonNumber > 0 || watchTarget.episodeNumber > 0) ->
                    watchTarget.seasonNumber
                isSerial -> details?.seasons?.firstOrNull()?.seasonNumber ?: 0
                else -> 0
            }
            val season = if (isSerial) {
                moviesRepository.getTvSeasonDetails(videoId = videoId, seasonNumber = seasonNumber)
            } else {
                null
            }
            val episode = season?.episodes?.firstOrNull { it.episodeNumber == watchTarget?.episodeNumber }
                ?: season?.episodes?.firstOrNull()

            analytics.track(AppEvents.videoOpened(videoId = videoId, isSerial = isSerial))
            tasteInteractor.recordSignal(videoId.toString(), isSerial, TasteSignalKind.OPENED)

            updateState {
                copy(
                    isLoading = false,
                    isEpisodesLoading = false,
                    isError = false,
                    videoInfo = details,
                    selectedEpisode = episode,
                    selectedSeason = season,
                    isFavorite = isFavorite,
                    watchTarget = watchTarget,
                )
            }
        }
    }

    private fun loadCast() {
        launch {
            val videoCast = if (isSerial) {
                moviesRepository.getTvCast(videoId = videoId)
            } else {
                moviesRepository.getMovieCast(videoId = videoId)
            }

            updateState {
                copy(videoCast = videoCast)
            }
        }
    }

    private fun loadSimilarVideo() {
        launch {
            val similarMovie = if (isSerial) {
                moviesRepository.getSimilarTv(videoId = videoId)
            } else {
                moviesRepository.getSimilarVideos(videoId = videoId)
            }

            updateState {
                copy(similarMovie = similarMovie)
            }
        }
    }

    private fun loadSeason(videoId: Int, seasonNumber: Int) {
        viewModelScope.launch(coroutineExceptionHandler) {
            val requestSeason = moviesRepository.getTvSeasonDetails(videoId, seasonNumber)
            updateState {
                val info = videoInfo
                info ?: return@updateState copy(isLoading = false, isError = true)
                copy(
                    isLoading = false,
                    isEpisodesLoading = false,
                    isError = false,
                    videoInfo = info.copy(
                        seasons = info.seasons?.map { season ->
                            requestSeason?.let {
                                if (season.seasonNumber == seasonNumber) requestSeason else season
                            } ?: season
                        }
                    ),
                    selectedSeason = requestSeason ?: selectedSeason,
                    selectedEpisode = requestSeason?.episodes?.firstOrNull()
                )
            }
        }
    }

    private fun featuresInit() {
        launch {
            globalStateRepository.features.collect {
                updateState { copy(features = it) }
            }
        }
    }
}
